import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sun, Moon, LogIn, LogOut, Clock, Cog, Plus, ChevronLeft, ChevronRight, ChevronDown, ArrowRight } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import attendanceApi, { extraMachineApi, ShiftType, AttendanceEntry, AttendanceStatus, ExtraMachineRequest } from '../services/attendanceApi';
import { useAuth } from '../contexts/AuthContext';
import KpiRow, { Kpi } from '../components/workforce/KpiRow';
import AttendanceCalendar from '../components/workforce/AttendanceCalendar';
import { StatusBadge, ShiftLabel, fmt, fmtTime, rupee } from '../components/workforce/shared';

// Duration between clock-in and clock-out, e.g. "12h 47m" (— when the shift is still open).
const durationLabel = (inAt?: string | null, outAt?: string | null): string => {
  if (!inAt || !outAt) return '—';
  const mins = dayjs(outAt).diff(dayjs(inAt), 'minute');
  if (mins <= 0) return '—';
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};
const durationMins = (inAt?: string | null, outAt?: string | null): number =>
  (inAt && outAt) ? Math.max(0, dayjs(outAt).diff(dayjs(inAt), 'minute')) : 0;
// True when clock-out falls on a later calendar day than clock-in (e.g. a night shift
// that ends the next morning) — worth flagging so the times don't read as same-day.
const isNextDay = (inAt?: string | null, outAt?: string | null): boolean =>
  !!(inAt && outAt) && dayjs(outAt).format('YYYY-MM-DD') !== dayjs(inAt).format('YYYY-MM-DD');

const MyAttendance: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shiftType, setShiftType] = useState<ShiftType>('DAY');
  // Which shift-log entry is expanded on mobile (accordion; null = all collapsed).
  const [openEntry, setOpenEntry] = useState<number | null>(null);

  // Month drives the KPI row, payable card, calendar and extra-machine list.
  const [emMonth, setEmMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const [showEmForm, setShowEmForm] = useState(false);
  const [emDate, setEmDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [emCount, setEmCount] = useState<string>('1');
  const [emNote, setEmNote] = useState<string>('');

  const hasEmployeeProfile = !!user?.employee?.id;

  const { data: currentData, isLoading: loadingCurrent } = useQuery({
    queryKey: ['attendance', 'current'],
    queryFn: () => attendanceApi.getMyCurrent(),
    enabled: hasEmployeeProfile,
  });

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['attendance', 'my-history'],
    queryFn: () => attendanceApi.getMyHistory(),
    enabled: hasEmployeeProfile,
  });

  const { data: summaryData, isLoading: loadingSummary } = useQuery({
    queryKey: ['attendance', 'my-summary', emMonth],
    queryFn: () => attendanceApi.getMySummary(emMonth),
    enabled: hasEmployeeProfile,
  });

  const { data: myExtraData } = useQuery({
    queryKey: ['extra-machines', 'mine', emMonth],
    queryFn: () => extraMachineApi.getMine(emMonth),
    enabled: hasEmployeeProfile,
  });

  const current: AttendanceEntry | null = currentData?.current ?? null;
  const history: AttendanceEntry[] = historyData?.entries ?? [];
  const summary = summaryData?.summary ?? null;
  const myExtra: ExtraMachineRequest[] = myExtraData?.requests ?? [];

  // Hours clocked in the selected month (self-view has no server-side hours, so derive
  // it from completed clock-in→clock-out pairs in history).
  const monthHours = useMemo(() => {
    const mins = history
      .filter((e) => dayjs(e.clockInAt).format('YYYY-MM') === emMonth)
      .reduce((a, e) => a + durationMins(e.clockInAt, e.clockOutAt), 0);
    return Math.round(mins / 60);
  }, [history, emMonth]);

  const createExtraMutation = useMutation({
    mutationFn: () => extraMachineApi.create({
      workDate: emDate,
      quantity: Number(emCount),
      note: emNote.trim() || undefined,
    }),
    onSuccess: (res) => {
      toast.success(res.message || 'Request submitted');
      setShowEmForm(false);
      setEmNote('');
      setEmCount('1');
      queryClient.invalidateQueries({ queryKey: ['extra-machines', 'mine'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to submit request'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['attendance', 'current'] });
    queryClient.invalidateQueries({ queryKey: ['attendance', 'my-history'] });
  };

  const clockInMutation = useMutation({
    mutationFn: () => attendanceApi.clockIn(shiftType),
    onSuccess: (res) => { toast.success(res.message || 'Clocked in'); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to clock in'),
  });

  const clockOutMutation = useMutation({
    mutationFn: () => attendanceApi.clockOut(),
    onSuccess: (res) => { toast.success(res.message || 'Clocked out'); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to clock out'),
  });

  if (!hasEmployeeProfile) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">My Attendance</h1>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm">
          Your account is not linked to an employee profile, so attendance is unavailable.
          Please ask an admin to set up your employee record.
        </div>
      </div>
    );
  }

  const isBusy = clockInMutation.isPending || clockOutMutation.isPending;
  const extraUnitLabel = summary?.extraUnit === 'HOUR' ? 'hours' : 'machines';

  // ── Header KPI row (month-scoped) ──
  const kpis: Kpi[] = [
    { label: 'Present', value: summary?.presentDays ?? 0, note: 'days this month', tone: 'good' },
    { label: 'Absent', value: summary?.absentDays ?? 0, note: 'days this month', tone: (summary?.absentDays ?? 0) > 0 ? 'danger' : 'default' },
    { label: 'Hours', value: `${monthHours}h`, note: 'clocked this month' },
    { label: 'This-month pay', value: rupee(summary?.computedSalary), note: 'base + approved extras', tone: 'good' },
  ];

  const monthNav = (
    <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white shrink-0">
      <button
        onClick={() => setEmMonth(dayjs(`${emMonth}-01`).subtract(1, 'month').format('YYYY-MM'))}
        className="px-2 py-2 text-gray-600 hover:bg-gray-50 rounded-l-lg"
        title="Previous month"
      ><ChevronLeft className="h-4 w-4" /></button>
      <span className="px-3 py-2 text-sm font-medium text-gray-800 min-w-[120px] text-center">
        {dayjs(`${emMonth}-01`).format('MMMM YYYY')}
      </span>
      <button
        onClick={() => setEmMonth(dayjs(`${emMonth}-01`).add(1, 'month').format('YYYY-MM'))}
        className="px-2 py-2 text-gray-600 hover:bg-gray-50 rounded-r-lg"
        title="Next month"
      ><ChevronRight className="h-4 w-4" /></button>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-900">My Attendance</h1>
        {monthNav}
      </div>

      {/* KPI row */}
      <KpiRow items={kpis} />

      {/* Clock in / out */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        {loadingCurrent ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : current ? (
          <div>
            <div className="flex items-start gap-3">
              <div className={`shrink-0 h-11 w-11 rounded-full flex items-center justify-center ${current.status === 'APPROVED' ? 'bg-green-100' : 'bg-amber-100'}`}>
                <Clock className={`h-6 w-6 ${current.status === 'APPROVED' ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-gray-900">
                    {current.status === 'APPROVED' ? 'You are clocked in' : 'Awaiting approval'}
                  </span>
                  <StatusBadge status={current.status} />
                </div>
                <div className="text-sm text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                  <ShiftLabel shift={current.shiftType} />
                  <span>· since {fmt(current.clockInAt)}</span>
                </div>
              </div>
            </div>

            {current.status === 'PENDING' && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-4">
                Waiting for a manager to approve your clock-in.
              </p>
            )}

            <button
              onClick={() => clockOutMutation.mutate()}
              disabled={isBusy}
              className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
            >
              <LogOut className="h-5 w-5" /> Clock Out
            </button>
          </div>
        ) : (
          <div>
            <div className="font-semibold text-gray-900 mb-1">You are clocked out</div>
            <div className="text-sm text-gray-500 mb-4">Select your shift and clock in.</div>

            <div className="flex gap-3 mb-5 max-w-md">
              {(['DAY', 'NIGHT'] as ShiftType[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setShiftType(s)}
                  className={`flex-1 flex flex-col items-center justify-center gap-1 px-4 py-4 rounded-lg border-2 font-medium transition-colors ${
                    shiftType === s
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {s === 'DAY' ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
                  <span className="text-sm">{s === 'DAY' ? 'Day Shift' : 'Night Shift'}</span>
                </button>
              ))}
            </div>

            <button
              onClick={() => clockInMutation.mutate()}
              disabled={isBusy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
            >
              <LogIn className="h-5 w-5" /> Clock In
            </button>
          </div>
        )}
      </div>

      {/* Payable + Attendance calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6 items-start">
        {/* Payable */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Payable — {dayjs(`${emMonth}-01`).format('MMMM YYYY')}</h3>
          {loadingSummary ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
            </div>
          ) : summary ? (
            <>
              <div className="text-3xl font-bold text-gray-900">{rupee(summary.computedSalary)}</div>
              <div className="text-xs text-gray-500 mt-1">Prorated by the days you were present this month.</div>
              <div className="border-t border-gray-100 my-4" />
              <div className="space-y-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-gray-600">{summary.payType === 'METER' ? 'Meter earnings' : 'Monthly base'}</div>
                    <div className="text-xs text-gray-400">
                      {summary.payType === 'METER'
                        ? `${summary.meters} m × rate`
                        : `${rupee(summary.monthlyRate ?? 0)} ÷ 30 × ${summary.presentDays} present`}
                    </div>
                  </div>
                  <span className="font-medium text-gray-900">{rupee(summary.basePay)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-gray-600">Extra {extraUnitLabel}</span>
                  <span className="font-medium text-gray-900">{summary.extraQuantity} × {rupee(summary.extraRate)} = {rupee(summary.extrasAmount)}</span>
                </div>
                <div className="border-t border-gray-100 pt-2.5 flex items-baseline justify-between gap-3">
                  <span className="font-semibold text-gray-800">Total payable</span>
                  <span className="text-lg font-bold text-blue-600">{rupee(summary.computedSalary)}</span>
                </div>
              </div>
              <div className="mt-4 px-3 py-2.5 bg-gray-50 border-l-2 border-amber-500 text-xs text-gray-600 leading-relaxed">
                Base pay is your monthly rate ÷ 30 × days present, so absences reduce it. Approved extra-machine runs are added on top.
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-8 text-sm">No data for this month.</div>
          )}
        </div>

        {/* Attendance calendar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Attendance — {dayjs(`${emMonth}-01`).format('MMMM YYYY')}</h3>
          {loadingSummary ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
            </div>
          ) : (
            <AttendanceCalendar month={emMonth} summary={summary} />
          )}
        </div>
      </div>

      {/* Extra machines */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Cog className="h-5 w-5 text-gray-400" /> Extra {summary?.extraUnit === 'HOUR' ? 'Hours' : 'Machines'}
          </h2>
          <button
            onClick={() => { setShowEmForm((v) => !v); setEmDate(dayjs().format('YYYY-MM-DD')); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Request
          </button>
        </div>

        {showEmForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date</label>
                <input
                  type="date"
                  value={emDate}
                  max={dayjs().format('YYYY-MM-DD')}
                  onChange={(e) => setEmDate(e.target.value)}
                  className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Extra {extraUnitLabel}</label>
                <input
                  type="number"
                  min={summary?.extraUnit === 'HOUR' ? '0.5' : '1'}
                  step={summary?.extraUnit === 'HOUR' ? '0.5' : '1'}
                  value={emCount}
                  onChange={(e) => setEmCount(e.target.value)}
                  className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Note (optional)</label>
                <input
                  type="text"
                  value={emNote}
                  onChange={(e) => setEmNote(e.target.value)}
                  placeholder="e.g. covered for absent karigar"
                  className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowEmForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!emDate) { toast.error('Please pick a date'); return; }
                  if (!(Number(emCount) > 0)) { toast.error(`Enter the extra ${extraUnitLabel}`); return; }
                  createExtraMutation.mutate();
                }}
                disabled={createExtraMutation.isPending}
                className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createExtraMutation.isPending ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        )}

        {myExtra.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 text-center text-gray-400 py-8 text-sm">
            No extra-work requests this month.
          </div>
        ) : (
          <div className="space-y-2">
            {myExtra.map((r) => (
              <div key={r.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      <Cog className="h-3.5 w-3.5" /> {r.quantity}{r.unit === 'HOUR' ? ' hr' : ''}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{dayjs(r.workDate).format('DD MMM YYYY')}</span>
                  </div>
                  {r.note && <div className="text-xs text-gray-500 mt-1 truncate">{r.note}</div>}
                  {r.status === 'REJECTED' && r.rejectReason && (
                    <div className="text-xs text-red-500 mt-1">Reason: {r.rejectReason}</div>
                  )}
                </div>
                <StatusBadge status={r.status as AttendanceStatus} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shift log (history) */}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-base font-semibold text-gray-900">Shift log</h2>
        <span className="text-xs text-gray-500">your recent clock-ins</span>
      </div>

      {loadingHistory ? (
        <div className="text-center text-gray-400 py-8">Loading…</div>
      ) : history.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 text-center text-gray-400 py-8">
          No attendance records yet.
        </div>
      ) : (
        <>
          {/* Mobile: collapsible list — compact date-first rows, tap to expand details. */}
          <div className="space-y-2 sm:hidden">
            {history.map((e) => {
              const open = openEntry === e.id;
              return (
                <div key={e.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Collapsed header (always visible) */}
                  <button
                    type="button"
                    onClick={() => setOpenEntry(open ? null : e.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    aria-expanded={open}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">{dayjs(e.clockInAt).format('ddd, DD MMM YYYY')}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                        <ShiftLabel shift={e.shiftType} />
                        <span className="text-gray-300">·</span>
                        <span>{durationLabel(e.clockInAt, e.clockOutAt)}</span>
                      </div>
                    </div>
                    <StatusBadge status={e.status} />
                    <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Expanded detail */}
                  {open && (
                    <div className="px-4 pb-4">
                      <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">Clock in</div>
                          <div className="text-sm font-medium text-gray-800 whitespace-nowrap">{fmtTime(e.clockInAt)}</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />
                        <div className="flex-1 min-w-0 text-right">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">
                            Clock out{isNextDay(e.clockInAt, e.clockOutAt) && <span className="text-amber-600 normal-case font-medium"> · next day</span>}
                          </div>
                          <div className="text-sm font-medium text-gray-800 whitespace-nowrap">{fmtTime(e.clockOutAt)}</div>
                        </div>
                      </div>
                      {e.approver?.name && (
                        <div className="mt-2 text-xs text-gray-400">Approved by {e.approver.name}</div>
                      )}
                      {e.status === 'REJECTED' && e.rejectReason && (
                        <div className="mt-2 text-xs text-red-500">Reason: {e.rejectReason}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop: shift-log table */}
          <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Date', 'Shift', 'In / Out', 'Duration', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{dayjs(e.clockInAt).format('DD MMM YYYY')}</td>
                      <td className="px-4 py-3"><ShiftLabel shift={e.shiftType} /></td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {fmtTime(e.clockInAt)} → {fmtTime(e.clockOutAt)}
                        {isNextDay(e.clockInAt, e.clockOutAt) && <span className="ml-1 text-xs text-amber-600">(+1 day)</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{durationLabel(e.clockInAt, e.clockOutAt)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={e.status} />
                        {e.status === 'REJECTED' && e.rejectReason ? (
                          <div className="text-xs text-red-500 mt-0.5">{e.rejectReason}</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MyAttendance;
